'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { AudioDom } from '@/app/components/shared/AudioDom';
import { Button } from '@/app/components/ui/button';
import type { TTSVoiceOption } from '@/app/constants';
import { IMG_DEFAULT } from '@/app/constants';
import { cn, getVoiceCloneWatermarkText } from '@/app/utils';

interface VoiceOptionItemProps {
  audio: TTSVoiceOption;
  selected: boolean;
  onSelect: (audioId: string) => void;
  languageName?: string;
  variant?: 'default' | 'figma';
  onEdit?: (audioId: string) => void;
}

export const VoiceOptionItem: FC<VoiceOptionItemProps> = ({
  audio,
  selected,
  onSelect,
  languageName,
  variant = 'default',
  onEdit,
}) => {
  const t = useTranslations();
  const shouldShowWatermark = 'is_my' in audio && audio.is_my === true;

  if (variant === 'figma') {
    return (
      <div
        className={cn(
          'group flex w-full cursor-pointer items-center justify-between rounded-12 px-3 py-2 transition-colors hover:bg-system-white04',
          selected && 'border border-system-white08 bg-system-white08 hover:bg-system-white08',
        )}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (audio.id) {
            onSelect(audio.id);
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <AudioDom
            audio={{ ...audio, img: audio.img || IMG_DEFAULT }}
            className="size-8 rounded-full"
            playIconClassName="size-4"
            imageOverlay
          />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2 text-sm leading-[1.6]">
              <span className="truncate text-system-white">
                {audio.name}
                {languageName ? ` - ${languageName}` : ''}
              </span>
              {shouldShowWatermark && <WatermarkLabel />}
            </div>
            <div className="truncate text-system-white/[.32] text-xs leading-[18px]">{audio.description}</div>
          </div>
        </div>
        {onEdit ? (
          <button
            type="button"
            aria-label={t('tools.voiceClone.voiceInfo.edit')}
            className={cn(
              'ml-3 flex size-6 shrink-0 items-center justify-center rounded-full text-system-white/72 opacity-0 transition hover:bg-system-white08 hover:text-system-white group-hover:opacity-100',
              selected && 'opacity-100',
            )}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (audio.id) {
                onEdit(audio.id);
              }
            }}
          >
            <BaseLineEdit className="size-4" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex w-full cursor-pointer items-center gap-3 rounded-12 p-2 transition-colors hover:bg-system-hover01"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (audio.id) {
          onSelect(audio.id);
        }
      }}
    >
      <AudioDom audio={{ ...audio, img: audio.img || IMG_DEFAULT }} className="size-11 rounded-12" imageOverlay />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2 text-sm leading-6">
          <span className="truncate text-system-white">
            {audio.name}
            {languageName ? ` - ${languageName}` : ''}
          </span>
          {shouldShowWatermark && <WatermarkLabel />}
        </div>
        <div className="truncate text-system-white/40 text-xs">{audio.description}</div>
      </div>
      {selected ? (
        <div className="ml-3 flex h-8 min-w-[72px] shrink-0 items-center justify-center rounded-12 border border-system-white08 bg-system-white08 px-4 text-sm text-system-white">
          <BaseLineCheck2 className="size-5 text-system-white" />
        </div>
      ) : (
        <Button className="ml-3 h-8 min-w-[72px] shrink-0 px-4 text-xs" variant="outline">
          {t('elements.elementDialog.select')}
        </Button>
      )}
    </div>
  );
};

function WatermarkLabel() {
  return <span className="shrink-0 text-system-white/40 text-xs leading-[1.4]">{getVoiceCloneWatermarkText()}</span>;
}
